import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import GridLayout from "react-grid-layout";
import { useDispatch } from "react-redux";
import { useTypedSelector } from "../../hooks.tsx";
import {
  getSelectedDashboard,
  getSelectedDashboardTileIds,
  saveDashboardTileLayouts,
} from "../../store/conversation.tsx";
import { DashboardTile } from "../../store/types.tsx";
import { tallyTokens } from "../../theme.tsx";
import DashboardTileCard from "./DashboardTileCard.tsx";
import DashboardTilePlaceholderCard from "./DashboardTilePlaceholderCard.tsx";
import DashboardPrintView from "./DashboardPrintView.tsx";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "../../styles/dashboard-grid.css";

const DESKTOP_DASHBOARD_COLUMNS = 12;
const DESKTOP_ROW_HEIGHT = 28;
const DESKTOP_GRID_MARGIN = 18;
/** Window height the row height and gaps above were designed at. Taller
 *  windows (large screens) scale rows and gaps by the same factor, so tiles
 *  grow in height as the grid's 12 columns grow in width - two rows of tiles
 *  still fit on screen. Saved layouts are in rows and columns, so they are
 *  unaffected. */
const DESKTOP_REFERENCE_WINDOW_HEIGHT = 900;

const useWindowHeight = () => {
  const [height, setHeight] = React.useState(() => window.innerHeight);

  React.useEffect(() => {
    const onResize = () => setHeight(window.innerHeight);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return height;
};

type GridLayoutItem = {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
};

const getTileSizeConstraints = (type?: string) => {
  if (type === "Graph") {
    return { w: 6, h: 12, minW: 4, minH: 9 };
  }

  if (type === "Table") {
    return { w: 8, h: 12, minW: 5, minH: 9 };
  }

  return { w: 4, h: 7, minW: 3, minH: 5 };
};

const normalizeGridLayout = (
  layout: Array<Pick<GridLayoutItem, "i" | "x" | "y" | "w" | "h">>,
  tileTypesById: Record<string, string>,
): GridLayoutItem[] =>
  layout.map((item) => ({
    ...item,
    ...getTileSizeConstraints(tileTypesById[item.i]),
    x: item.x,
    y: item.y,
    w: item.w,
    h: item.h,
  }));

const collides = (
  left: Pick<GridLayoutItem, "x" | "y" | "w" | "h">,
  right: Pick<GridLayoutItem, "x" | "y" | "w" | "h">,
) =>
  left.x < right.x + right.w &&
  left.x + left.w > right.x &&
  left.y < right.y + right.h &&
  left.y + left.h > right.y;

const findOpenPosition = (
  width: number,
  height: number,
  placedTiles: GridLayoutItem[],
) => {
  let y = 0;

  while (y < 1000) {
    for (let x = 0; x <= DESKTOP_DASHBOARD_COLUMNS - width; x += 1) {
      const candidate = { x, y, w: width, h: height };
      const hasCollision = placedTiles.some((placedTile) =>
        collides(candidate, placedTile),
      );
      if (!hasCollision) {
        return { x, y };
      }
    }
    y += 1;
  }

  return { x: 0, y };
};

const buildGridLayout = (
  tileIds: string[],
  tileTypesById: Record<string, string>,
  tileLayoutsById: Record<string, DashboardTile["layout"]>,
): GridLayoutItem[] => {
  const placedTiles: GridLayoutItem[] = [];

  return tileIds.map((tileId) => {
    const savedLayout = tileLayoutsById[tileId];
    if (savedLayout) {
      const defaults = getTileSizeConstraints(tileTypesById[tileId]);
      const item = {
        i: tileId,
        x: savedLayout.column,
        y: savedLayout.row,
        w: savedLayout.column_span,
        h: savedLayout.row_span,
        minW: defaults.minW,
        minH: defaults.minH,
      };
      placedTiles.push(item);
      return item;
    }

    const defaults = getTileSizeConstraints(tileTypesById[tileId]);
    const openPosition = findOpenPosition(defaults.w, defaults.h, placedTiles);

    const item: GridLayoutItem = {
      i: tileId,
      x: openPosition.x,
      y: openPosition.y,
      ...defaults,
    };

    placedTiles.push(item);
    return item;
  });
};

const sortTileIdsByLayout = (
  tileIds: string[],
  tileLayoutsById: Record<string, DashboardTile["layout"]>,
) => {
  const tileOrder = tileIds.reduce(
    (accumulator, tileId, index) => {
      accumulator[tileId] = index;
      return accumulator;
    },
    {} as Record<string, number>,
  );

  return [...tileIds].sort((leftId, rightId) => {
    const left = tileLayoutsById[leftId];
    const right = tileLayoutsById[rightId];

    if (!left && !right) {
      return tileOrder[leftId] - tileOrder[rightId];
    }
    if (!left) {
      return 1;
    }
    if (!right) {
      return -1;
    }
    if (left.row !== right.row) {
      return left.row - right.row;
    }
    if (left.column !== right.column) {
      return left.column - right.column;
    }
    return tileOrder[leftId] - tileOrder[rightId];
  });
};

// Compares against the layout the page opened with, not only the saved one:
// tiles that were never placed get an automatic position on load, and that
// alone is not a change the user made.
const hasUnsavedLayoutChanges = (
  layout: GridLayoutItem[],
  initialLayout: GridLayoutItem[],
) =>
  layout.some((item) => {
    const initialItem = initialLayout.find(
      (candidate) => candidate.i === item.i,
    );
    return (
      !initialItem ||
      initialItem.x !== item.x ||
      initialItem.y !== item.y ||
      initialItem.w !== item.w ||
      initialItem.h !== item.h
    );
  });

const useGridWidth = () => {
  const [containerNode, setContainerNode] =
    React.useState<HTMLDivElement | null>(null);
  const [width, setWidth] = React.useState<number | null>(null);

  const containerRef = React.useCallback((node: HTMLDivElement | null) => {
    setContainerNode(node);
  }, []);

  React.useLayoutEffect(() => {
    if (!containerNode || typeof window === "undefined") {
      return;
    }

    const measure = () => {
      if (!containerNode) {
        return;
      }

      const nextWidth = Math.round(containerNode.getBoundingClientRect().width);
      if (nextWidth > 0) {
        setWidth(nextWidth);
      }
    };

    measure();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(() => {
        measure();
      });
      observer.observe(containerNode);

      return () => observer.disconnect();
    }

    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [containerNode]);

  return { containerRef, width };
};

const RELATIVE_TIME_UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ["year", 365 * 24 * 60 * 60],
  ["month", 30 * 24 * 60 * 60],
  ["week", 7 * 24 * 60 * 60],
  ["day", 24 * 60 * 60],
  ["hour", 60 * 60],
  ["minute", 60],
];

/** "4 minutes ago" / "for 4 minutter siden" in the active locale. */
const formatRelativeTime = (isoDate: string, locale: string) => {
  const seconds = (new Date(isoDate).getTime() - Date.now()) / 1000;
  if (Number.isNaN(seconds)) {
    return null;
  }

  const format = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  for (const [unit, unitSeconds] of RELATIVE_TIME_UNITS) {
    if (Math.abs(seconds) >= unitSeconds) {
      return format.format(Math.round(seconds / unitSeconds), unit);
    }
  }
  return format.format(0, "minute");
};

/** "Sep 2026" / "sep. 2026" in the active locale. */
const formatMonthYear = (isoDate: string, locale: string) => {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat(locale, {
    month: "short",
    year: "numeric",
  }).format(date);
};

/** "3 Oct 2026, 10:04" in the active locale - absolute, because a PDF is
 *  read long after it was made. */
const formatDateTime = (isoDate: string, locale: string) => {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

/** Time for the print copy's charts to measure, render and finish their
 *  entry animation before the print dialog snapshots them. */
const PRINT_RENDER_DELAY_MS = 1200;

// Line icons drawn on the same 24px grid and stroke as the draft's header.
const CalendarIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    aria-hidden="true"
  >
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M8 3v4M16 3v4M3 10h18" strokeLinecap="round" />
  </svg>
);

const DownloadIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    aria-hidden="true"
  >
    <path
      d="M12 3v12m0 0 4-4m-4 4-4-4M4 19h16"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DashboardView: React.FC = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const dashboard = useTypedSelector((state) => getSelectedDashboard(state));
  const tileIds = useTypedSelector((state) =>
    getSelectedDashboardTileIds(state),
  );
  const dashboardTilesById = useTypedSelector(
    (state) => state.conversationState.dashboardTilesById,
  );
  const dashboardTileTypesById = useTypedSelector(
    (state) => state.conversationState.dashboardTileTypesById,
  );
  const dashboardTileLayoutsById = useTypedSelector(
    (state) => state.conversationState.dashboardTileLayoutsById,
  );
  const isLoading = useTypedSelector((state) =>
    dashboard
      ? state.conversationState.dashboardLoadingById[dashboard.id]
      : false,
  );
  const { containerRef: gridContainerRef, width: gridWidth } = useGridWidth();
  const windowHeight = useWindowHeight();
  const gridScale = Math.max(1, windowHeight / DESKTOP_REFERENCE_WINDOW_HEIGHT);
  const persistedLayoutsByTileId = React.useMemo(
    () =>
      tileIds.reduce(
        (accumulator, tileId) => {
          accumulator[tileId] =
            dashboardTileLayoutsById[tileId] ||
            dashboardTilesById[tileId]?.layout ||
            null;
          return accumulator;
        },
        {} as Record<string, DashboardTile["layout"]>,
      ),
    [tileIds, dashboardTileLayoutsById, dashboardTilesById],
  );

  const orderedTileIds = React.useMemo(
    () => sortTileIdsByLayout(tileIds, persistedLayoutsByTileId),
    [tileIds, persistedLayoutsByTileId],
  );

  const persistedGridLayout = React.useMemo(
    () =>
      buildGridLayout(
        orderedTileIds,
        dashboardTileTypesById,
        persistedLayoutsByTileId,
      ),
    [orderedTileIds, persistedLayoutsByTileId, dashboardTileTypesById],
  );
  const [draftLayout, setDraftLayout] =
    React.useState<GridLayoutItem[]>(persistedGridLayout);
  const [isSavingLayout, setIsSavingLayout] = React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);
  const dashboardTitle = dashboard?.title;

  // Exporting mounts the print copy, gives its charts time to draw, then opens
  // the browser's print dialog, where "Save as PDF" produces the file. The
  // page title becomes the suggested file name.
  React.useEffect(() => {
    if (!isExporting) {
      return;
    }

    const previousTitle = document.title;
    const root = document.documentElement;
    const restore = () => {
      document.title = previousTitle;
      // Switches the export-only print rules (dashboard-print.css) back off,
      // so a normal print of any page prints that page.
      root.classList.remove("dashboard-exporting");
    };
    const finish = () => {
      restore();
      setIsExporting(false);
    };
    window.addEventListener("afterprint", finish, { once: true });
    const timer = window.setTimeout(() => {
      document.title = dashboardTitle || previousTitle;
      root.classList.add("dashboard-exporting");
      window.print();
    }, PRINT_RENDER_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("afterprint", finish);
      restore();
    };
  }, [isExporting, dashboardTitle]);

  React.useEffect(() => {
    setDraftLayout(persistedGridLayout);
  }, [dashboard?.id, persistedGridLayout]);

  const handleLayoutChange = React.useCallback(
    (nextLayout: GridLayoutItem[]) => {
      setDraftLayout(normalizeGridLayout(nextLayout, dashboardTileTypesById));
    },
    [dashboardTileTypesById],
  );

  const layoutHasUnsavedChanges = React.useMemo(
    () => hasUnsavedLayoutChanges(draftLayout, persistedGridLayout),
    [draftLayout, persistedGridLayout],
  );

  const handleSaveLayout = React.useCallback(async () => {
    if (!dashboard || !layoutHasUnsavedChanges) {
      return;
    }

    setIsSavingLayout(true);
    try {
      const normalizedLayout = normalizeGridLayout(
        draftLayout,
        dashboardTileTypesById,
      );
      setDraftLayout(normalizedLayout);
      await (dispatch(
        saveDashboardTileLayouts({
          dashboardId: dashboard.id,
          layouts: normalizedLayout.map((item) => ({
            id: item.i,
            column: item.x,
            row: item.y,
            column_span: item.w,
            row_span: item.h,
          })),
        }) as any,
      ) as Promise<void>);
    } finally {
      setIsSavingLayout(false);
    }
  }, [
    dashboard,
    dashboardTileTypesById,
    draftLayout,
    dispatch,
    layoutHasUnsavedChanges,
  ]);

  if (!dashboard) {
    return null;
  }

  // Freshness of the data, so the newest tile wins; the dashboard's own
  // timestamp only moves when its layout or membership changes.
  const lastUpdatedAt = tileIds.reduce((latest, tileId) => {
    const tileUpdatedAt = dashboardTilesById[tileId]?.last_updated_at;
    return tileUpdatedAt && tileUpdatedAt > latest ? tileUpdatedAt : latest;
  }, dashboard.last_updated_at);
  const updatedAgo = formatRelativeTime(lastUpdatedAt, i18n.locale || "en");
  const createdOn = formatMonthYear(dashboard.created_at, i18n.locale || "en");
  const tileCountLabel =
    dashboard.tile_count === 1
      ? i18n._(msg`${dashboard.tile_count} tile`)
      : i18n._(msg`${dashboard.tile_count} tiles`);
  const updatedOn = formatDateTime(lastUpdatedAt, i18n.locale || "en");

  return (
    <Box sx={{ width: "100%", paddingBottom: 4 }}>
      {/* Page header: a plain title and a muted summary line, with page
          actions on the right - no surrounding panel. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          flexWrap: "wrap",
          gap: "14px",
          marginBottom: "6px",
        }}
      >
        <Typography
          component="h1"
          sx={{
            margin: 0,
            minWidth: 0,
            fontSize: "25px",
            // Geomanist ships in Regular only; 600 would be synthetic bold.
            fontWeight: 400,
            lineHeight: 1.55,
            letterSpacing: "-0.02em",
            color: tallyTokens.ink,
            textWrap: "balance",
          }}
        >
          {dashboard.title}
        </Typography>
        <Box
          sx={{
            // Right-aligned beside the title; on phones it wraps under the
            // title, where it reads better left-aligned.
            marginLeft: { xs: 0, md: "auto" },
            display: "flex",
            alignItems: "center",
            gap: "7px",
          }}
        >
          {createdOn && (
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "5px 10px",
                borderRadius: `${tallyTokens.radius.pill}px`,
                border: `1px solid ${tallyTokens.line}`,
                backgroundColor: tallyTokens.surface,
                color: tallyTokens.ink2,
                fontSize: "13px",
                // 24px line + padding + border = 36px, level with the button.
                lineHeight: "24px",
                whiteSpace: "nowrap",
              }}
            >
              <CalendarIcon />
              {i18n._(msg`Created ${createdOn}`)}
            </Box>
          )}
          <Button
            onClick={() => setIsExporting(true)}
            disabled={isExporting || isLoading || tileIds.length === 0}
            startIcon={<DownloadIcon />}
            sx={{
              gap: "6px",
              padding: "5px 10px",
              borderRadius: `${tallyTokens.radius.pill}px`,
              border: `1px solid ${tallyTokens.line}`,
              backgroundColor: tallyTokens.surface,
              color: tallyTokens.ink2,
              fontSize: "13px",
              lineHeight: "24px",
              textTransform: "none",
              whiteSpace: "nowrap",
              "& .MuiButton-startIcon": { margin: 0 },
              "&:hover": {
                borderColor: tallyTokens.lineStrong,
                backgroundColor: tallyTokens.surface,
                color: tallyTokens.ink,
              },
              "&.Mui-disabled": {
                color: tallyTokens.ink3,
              },
            }}
          >
            {isExporting
              ? i18n._(msg`Preparing PDF...`)
              : i18n._(msg`Export PDF`)}
          </Button>
          {/* The grid can only be rearranged on desktop, so there is nothing
              to save on smaller screens. */}
          {isDesktop && (
            <Button
              onClick={handleSaveLayout}
              disabled={!layoutHasUnsavedChanges || isSavingLayout}
              sx={{
                padding: "7px 13px",
                borderRadius: `${tallyTokens.radius.md}px`,
                border: `1px solid ${tallyTokens.brand}`,
                backgroundColor: tallyTokens.brand,
                color: "#fff",
                fontSize: "13px",
                lineHeight: 1.55,
                textTransform: "none",
                whiteSpace: "nowrap",
                "&:hover": {
                  backgroundColor: tallyTokens.brand,
                  filter: "brightness(1.07)",
                },
                // Stays in place so the header does not jump; it reads as
                // inactive until a tile has been moved or resized.
                "&.Mui-disabled": {
                  color: "#fff",
                  opacity: 0.45,
                },
              }}
            >
              {isSavingLayout
                ? i18n._(msg`Saving...`)
                : i18n._(msg`Save layout`)}
            </Button>
          )}
        </Box>
      </Box>
      <Typography
        sx={{
          margin: "0 0 22px",
          fontSize: "14px",
          textAlign: "left",
          lineHeight: 1.55,
          color: tallyTokens.ink2,
        }}
      >
        {tileCountLabel}
        {updatedAgo && ` · ${i18n._(msg`updated ${updatedAgo}`)}`}
      </Typography>

      {isLoading && tileIds.length === 0 ? (
        <Box
          sx={{
            minHeight: 240,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            color: "rgba(47, 33, 24, 0.78)",
          }}
        >
          <CircularProgress size={34} thickness={4.5} />
          <Typography variant="body1">
            {i18n._(msg`Loading dashboard tiles...`)}
          </Typography>
        </Box>
      ) : tileIds.length === 0 ? (
        <Box
          sx={{
            borderRadius: "16px",
            padding: 4,
            border: "1px dashed rgba(169,125,88,0.55)",
            background: "rgba(255,255,255,0.72)",
          }}
        >
          <Typography variant="body1">
            {i18n._(
              msg`Save answers from your conversations to surface them here.`,
            )}
          </Typography>
        </Box>
      ) : isDesktop ? (
        <Box ref={gridContainerRef} sx={{ width: "100%" }}>
          {gridWidth !== null && (
            <GridLayout
              className="dashboard-grid-layout"
              layout={draftLayout}
              width={gridWidth}
              cols={DESKTOP_DASHBOARD_COLUMNS}
              rowHeight={DESKTOP_ROW_HEIGHT * gridScale}
              margin={[
                DESKTOP_GRID_MARGIN * gridScale,
                DESKTOP_GRID_MARGIN * gridScale,
              ]}
              containerPadding={[0, 0]}
              compactType={null}
              preventCollision
              isBounded
              draggableHandle=".dashboard-tile-drag-handle"
              draggableCancel=".dashboard-tile-action, .react-resizable-handle"
              resizeHandles={["se"]}
              onLayoutChange={handleLayoutChange}
            >
              {orderedTileIds.map((tileId) => {
                const tile = dashboardTilesById[tileId];
                if (!tile) {
                  return (
                    <div key={tileId} className="dashboard-grid-item">
                      <DashboardTilePlaceholderCard
                        type={dashboardTileTypesById[tileId]}
                        fillHeight
                      />
                    </div>
                  );
                }

                return (
                  <div key={tileId} className="dashboard-grid-item">
                    <DashboardTileCard tile={tile} fillHeight />
                  </div>
                );
              })}
            </GridLayout>
          )}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.25 }}>
          {orderedTileIds.map((tileId) => {
            const tile = dashboardTilesById[tileId];
            if (!tile) {
              return (
                <DashboardTilePlaceholderCard
                  key={tileId}
                  type={dashboardTileTypesById[tileId]}
                />
              );
            }

            return <DashboardTileCard key={tileId} tile={tile} />;
          })}
        </Box>
      )}

      {isExporting && (
        <DashboardPrintView
          title={dashboard.title}
          subtitle={
            updatedOn
              ? `${tileCountLabel} · ${i18n._(msg`updated ${updatedOn}`)}`
              : tileCountLabel
          }
          layout={draftLayout}
          tilesById={dashboardTilesById}
          tileTypesById={dashboardTileTypesById}
        />
      )}
    </Box>
  );
};

export default DashboardView;
